import React, { useEffect } from "react";
import { Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useParams, useRouteMatch } from "react-router-dom";
import { Typography } from "antd";
import BreadCrumbsWrapper from "./BreadCrumbs.style";
import { ReactComponent as EmployeesImg } from "../../assets/employees.svg";
import { ReactComponent as PatientsImg } from "../../assets/patients.svg";
import EmployeeDetailsHeader from "../../features/employeeDetails/components/EmployeeDetailsHeader";
import PatientDetailsHeader from "../../features/patientDetails/components/PatientDetailsHeader";
import EmployeeTitle from "../../features/employeeDetails/components/EmployeeTitle";
import PatientTitle from "../../features/patientDetails/components/PatientTitle";
import Summary from "../../features/salary/components/Summary";
import ProspectusesSummary from "../../features/prospectuses/components/Summary";
import OpportunityDetailsHeader from "../../features/crm/opportunity/OpportunityDetailsHeader";
import { getFullName } from "../../components/util/helpers";
import { useIntl } from "react-intl";

const { Title } = Typography;

const BreadCrumbs = (props) => {
  const { location } = props;
  const intl = useIntl();
  let { branchId } = useParams();
  const direction = useSelector(
    (state) => state.user.currentAppLocale.direction
  );
  const activeClient = useSelector((state) => state.clientDetails.activeClient);
  const activeEmployee = useSelector(
    (state) => state.employeeDetails.activeEmployee
  );
  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );
  const activeCampaign = useSelector((state) => state.campaign.activeCampaign);
  const activeReport = useSelector((state) => state.report.activeReport);
  const activeLead = useSelector((state) => state.lead.activeLead);

  const activeContact = useSelector((state) => state.contact.activeContact);
  const activeEmployeeName = getFullName(
    activeEmployee.surename,
    activeEmployee.first_name
  );
  const activePatientName = getFullName(
    activePatient.surename,
    activePatient.first_name
  );
  const activeLeadName = getFullName(
    activeLead.surename,
    activeLead.first_name
  );

  const activeContactName = getFullName(
    activeContact.surename,
    activeContact.first_name
  );
  const activeCampaignName = activeCampaign.name;

  const breadcrumbNameMap = {
    "/": intl.formatMessage({
      id: "app.breadcrumb.home",
    }),
    "/clients": intl.formatMessage({
      id: "app.breadcrumb.clients",
    }),
    "/clients/add": intl.formatMessage({
      id: "app.breadcrumb.addNewClient",
    }),
    [`/clients/${activeClient.id}`]: activeClient.name,
    "/employees":
      (location.state && location.state.staff && activeEmployee.staff_member) ||
      (activeEmployee.staff_member && !activeEmployee.care_giver)
        ? intl.formatMessage({
            id: "app.breadcrumb.staff",
          })
        : intl.formatMessage({
            id: "app.breadcrumb.employees",
          }),
    "/employees/add": intl.formatMessage({
      id: "app.breadcrumb.addNewEmployee",
    }),
    [`/employees/${activeEmployee.id}`]: activeEmployeeName,
    "/patients": intl.formatMessage({
      id: "app.breadcrumb.patients",
    }),
    "/patients/add": intl.formatMessage({
      id: "app.breadcrumb.addNewPatient",
    }),
    [`/patients/${activePatient.id}`]: activePatientName,
    [`/gaps`]: intl.formatMessage({
      id: "app.breadcrumb.gaps",
    }),
    [`/diaries_approval`]: intl.formatMessage({
      id: "app.breadcrumb.diaries_approval",
    }),
    [`/tasks`]: intl.formatMessage({
      id: "app.breadcrumb.tasks",
    }),
    [`/branch_salary`]: intl.formatMessage({
      id: "app.breadcrumb.salary",
    }),
    [`/accounting`]: intl.formatMessage({
      id: "app.breadcrumb.accounting",
    }),
    [`/ok2go_btl`]: intl.formatMessage({
      id: "app.breadcrumb.ok2go_btl",
    }),
    [`/ok2go_revaha`]: intl.formatMessage({
      id: "app.breadcrumb.ok2go_revaha",
    }),

    [`/reports`]: intl.formatMessage({
      id: "app.breadcrumb.reports",
    }),
    "/profile": intl.formatMessage({
      id: "app.breadcrumb.profile",
    }),
    "/assets": intl.formatMessage({
      id: "app.breadcrumb.assets",
    }),
    "/leads": intl.formatMessage({
      id: "app.breadcrumb.leads",
    }),
    [`/leads/${activeLead.id}`]: activeLeadName,
    "/crm_patients": intl.formatMessage({
      id: "app.breadcrumb.crm_patients",
    }),
    [`/crm_patients/${activePatient.id}`]: activePatientName,
    "/contacts": intl.formatMessage({
      id: "app.breadcrumb.contacts",
    }),
    [`/contacts/${activeContact.id}`]: activeContactName,
    "/campaigns": intl.formatMessage({
      id: "app.breadcrumb.campaigns",
    }),
    [`/campaigns/${activeCampaign.id}`]: activeCampaignName,
    "/day_center": intl.formatMessage({
      id: "app.breadcrumb.day_center",
    }),
    "/order_import": intl.formatMessage({
      id: "app.breadcrumb.order_import",
    }),
    "/events_schedule": intl.formatMessage({
      id: "app.breadcrumb.events_schedule",
    }),
  };

  const baseLocation = "branches/" + branchId;
  const path = location.pathname.replace(baseLocation, "");
  const pathSnippets = path.split("/").filter((i) => i);
  let title = "";
  const extraBreadcrumbItems = pathSnippets.map((_, index) => {
    const key = `/${pathSnippets.slice(0, index + 1).join("/")}`;
    const url = "/branches/" + branchId + key;
    title = breadcrumbNameMap[key];
    if (!breadcrumbNameMap[key]) {
      return null;
    }
    return (
      <Breadcrumb.Item key={url}>
        <Link
          to={{
            pathname: url,
            fromState: location.state,
          }}
        >
          {breadcrumbNameMap[key]}
        </Link>
      </Breadcrumb.Item>
    );
  });

  const breadcrumbItems = [
    <Breadcrumb.Item key="home">
      <Link to={`/branches/${branchId}`}>
        <FormattedMessage id="app.breadcrumb.home" />
      </Link>
    </Breadcrumb.Item>,
  ].concat(extraBreadcrumbItems);

  let Comp = null;
  let TitleComp = null;
  let employees = useRouteMatch("/branches/:branchId/employees");
  let employee = useRouteMatch("/branches/:branchId/employees/:id");
  // let staff = useRouteMatch("/branches/:branchId/employees/staff/:id");
  let patients = useRouteMatch("/branches/:branchId/patients");
  let patient = useRouteMatch("/branches/:branchId/patients/:id");
  let opportunity = useRouteMatch("/branches/:branchId/crm_patients/:id");
  let salary = useRouteMatch("/branches/:branchId/branch_salary");
  let accounting = useRouteMatch("/branches/:branchId/accounting");
  switch (location.pathname) {
    case employees && employees.url:
      Comp = () => <EmployeesImg className="img withTabs" />;
      break;
    case employee && employee.url:
      Comp = () => (
        <EmployeeDetailsHeader
          staff={location.state && location.state.staff}
          className={location.state && location.state.staff ? "" : "withTabs"}
        />
      );
      TitleComp = () => <EmployeeTitle activeEntity={activeEmployee} />;
      break;
    case patients && patients.url:
      Comp = () => <PatientsImg className="img" />;
      break;
    case patient && patient.url:
      Comp = () => <PatientDetailsHeader className="withTabs img" />;
      TitleComp = () => <PatientTitle />;
      break;
    case opportunity && opportunity.url:
      Comp = () => <OpportunityDetailsHeader className="withTabs" />;
      break;
    case salary && salary.url:
      Comp = () => <Summary />;
      break;
    case accounting && accounting.url:
      Comp = () => <ProspectusesSummary />;
      break;

    default:
      Comp = null;
      break;
  }

  useEffect(() => {
    if (
      extraBreadcrumbItems &&
      extraBreadcrumbItems.length > 0 &&
      extraBreadcrumbItems[extraBreadcrumbItems.length - 1] &&
      extraBreadcrumbItems[extraBreadcrumbItems.length - 1].props
    ) {
      const title =
        extraBreadcrumbItems[extraBreadcrumbItems.length - 1]?.props?.children
          ?.props?.children;
      if (document.title !== title) {
        document.title = title;
      }
    }
  }, [extraBreadcrumbItems]);

  return (
    <BreadCrumbsWrapper data-rtl={direction}>
      <div className="container">
        <div className="breadcrumbs">
          <Breadcrumb>{breadcrumbItems}</Breadcrumb>
          {TitleComp ? <TitleComp /> : <Title level={1}>{title}</Title>}
        </div>
        {Comp ? <Comp /> : null}
      </div>
    </BreadCrumbsWrapper>
  );
};

BreadCrumbs.propTypes = {
  location: PropTypes.object,
};

export default BreadCrumbs;
